/**
 * Single-flight access-token refresh, shared by every API transport.
 *
 * The refresh rides the httpOnly `pm_refresh` cookie (`credentials: 'include'`)
 * the API set at login. Every caller within a burst awaits the same inflight
 * promise so concurrent 401s (across RTK Query, raw fetches, and WebSocket
 * reconnects) never hammer `/auth/refresh` in parallel.
 *
 * This module has NO Redux dependency on purpose — callers decide what to do
 * with the result (baseQuery + authFetch dispatch it into the auth slice).
 * Returns the new access token, or null if refresh failed.
 */
import { env } from '@/lib/env';

const API_PREFIX = '/api/v1';

let inflightRefresh: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const response = await fetch(`${env.API_URL}${API_PREFIX}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!response.ok) return null;
      const data = (await response.json()) as { access_token?: string };
      return data.access_token ?? null;
    } catch {
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}
